/** biome-ignore-all lint/style/noProcessEnv: We need to access process.env for environment variables */
import { createEnv } from '@t3-oss/env-nextjs'
import { z } from 'zod'

const booleanString = z
  .string()
  .refine((s) => s === 'true' || s === 'false')
  .transform((s) => s === 'true')

const RELEWISE_ENV_KEYS = [
  'NEXT_PUBLIC_RELEWISE_DATASET_ID',
  'NEXT_PUBLIC_RELEWISE_API_KEY',
  'NEXT_PUBLIC_RELEWISE_SERVER_URL',
] as const

type RelewiseEnv = Partial<Record<(typeof RELEWISE_ENV_KEYS)[number], string>>

const requireAllRelewiseEnvOrNone = (values: RelewiseEnv, ctx: z.RefinementCtx) => {
  const missingKeys = RELEWISE_ENV_KEYS.filter((key) => !values[key])

  if (missingKeys.length === 0 || missingKeys.length === RELEWISE_ENV_KEYS.length) {
    return
  }

  for (const key of missingKeys) {
    ctx.addIssue({ code: 'custom', path: [key], message: 'Required when any NEXT_PUBLIC_RELEWISE_* variable is set' })
  }
}

export const env = createEnv({
  server: {
    NEXTAUTH_SECRET: z.string().min(64),
    SAP_URL: z.url(),
    POSTNORD_API_HOST: z.url(),
    POSTNORD_API_KEY: z.string().min(32),
    ECOM_AUTH_PATH: z.string(),
    ECOM_API: z.string(),
    ECOM_ASM_API: z.string(),
    ECOM_CLIENT_ID_DK: z.string(),
    ECOM_CLIENT_PASSWORD_DK: z.string(),
    ECOM_CLIENT_ID_NO: z.string(),
    ECOM_CLIENT_PASSWORD_NO: z.string(),
    ECOM_CLIENT_ID_SE: z.string(),
    ECOM_CLIENT_PASSWORD_SE: z.string(),
    ECOM_CLIENT_ID_FI: z.string(),
    ECOM_CLIENT_PASSWORD_FI: z.string(),
    ECOM_GTM_KEY: z.string().optional(),
    ECOM_SCHEMA_URL: z.url().optional(),
    ASM_SAP_SCHEMA_URL: z.url().optional(),
    BFF_SCHEMA_URL: z.url().optional(),
    LOG_LEVEL: z.enum(['fatal', 'error', 'warn', 'info', 'debug', 'trace', 'silent']).default('info'),
    // Shared secret for the Sanity publish webhook (/api/revalidate/sanity).
    // When unset the route refuses all revalidation requests.
    SANITY_REVALIDATE_SECRET: z.string().min(32).optional(),
    // Shared 'use cache' store (config/cache/cache-handler.mjs). When set at BUILD time the Redis
    // cache handler is registered in next.config.ts; when absent the app uses the
    // Next.js built-in per-process LRU (current behavior). Use rediss:// for Azure TLS.
    NEXT_REDIS_URL: z.url().optional(),
    // Force CSP into Report-Only mode without redeploying. Set to 'true' in App Service
    // config to roll back enforcement after a violation is observed in Datadog.
    CSP_REPORT_ONLY: z
      .string()
      .optional()
      .default('false')
      .transform((s) => s === 'true'),
  },
  client: {
    NEXT_PUBLIC_BFF_ANONYMOUS_TOKEN: z.string().min(64),
    NEXT_PUBLIC_BFF_URL: z.url(),
    NEXT_PUBLIC_DEFAULT_LANGUAGE: z.enum(['dk', 'en']).default('dk'),
    NEXT_PUBLIC_GTM_KEY: z.string().optional(),
    NEXT_PUBLIC_DATADOG_CLIENT_TOKEN: z.string().optional().default(''),
    NEXT_PUBLIC_DATADOG_APPLICATION_ID: z.string().optional().default(''),
    NEXT_PUBLIC_GTM_DEBUG: booleanString.optional(),
    // Relewise tracking is sent when all NEXT_PUBLIC_RELEWISE_* are set; setting only some fails validation.
    NEXT_PUBLIC_RELEWISE_DATASET_ID: z.string().optional(),
    // Ships in the browser bundle: use a tracking-only API Credential with allowed origins, never the master key.
    NEXT_PUBLIC_RELEWISE_API_KEY: z.string().trim().min(1).optional(),
    // Per-dataset Server URL from My Relewise; its origin is added to the CSP connect-src.
    NEXT_PUBLIC_RELEWISE_SERVER_URL: z.url({ protocol: /^https$/ }).optional(),
  },

  // For Next.js >= 13.4.4, you only need to destructure client variables:
  experimental__runtimeEnv: {
    NEXT_PUBLIC_DATADOG_CLIENT_TOKEN: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
    NEXT_PUBLIC_DATADOG_APPLICATION_ID: process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID,
    SANITY_PROJECT_ID: process.env.SANITY_PROJECT_ID,
    SANITY_DATASET: process.env.SANITY_DATASET,
    NEXT_PUBLIC_BFF_ANONYMOUS_TOKEN: process.env.NEXT_PUBLIC_BFF_ANONYMOUS_TOKEN,
    NEXT_PUBLIC_BFF_URL: process.env.NEXT_PUBLIC_BFF_URL,
    NEXT_PUBLIC_DEFAULT_LANGUAGE: process.env.NEXT_PUBLIC_DEFAULT_LANGUAGE,
    NEXT_PUBLIC_GTM_KEY: process.env.NEXT_PUBLIC_GTM_KEY,
    NEXT_PUBLIC_GTM_DEBUG: process.env.NEXT_PUBLIC_GTM_DEBUG,
    NEXT_PUBLIC_RELEWISE_DATASET_ID: process.env.NEXT_PUBLIC_RELEWISE_DATASET_ID,
    NEXT_PUBLIC_RELEWISE_API_KEY: process.env.NEXT_PUBLIC_RELEWISE_API_KEY,
    NEXT_PUBLIC_RELEWISE_SERVER_URL: process.env.NEXT_PUBLIC_RELEWISE_SERVER_URL,
    NODE_ENV: process.env.NODE_ENV,
    AZ_ENV: process.env.AZ_ENV,
    DD_SERVICE: process.env.DD_SERVICE,
    DD_ENV: process.env.DD_ENV,
    DD_SITE: process.env.DD_SITE,
  },
  shared: {
    NODE_ENV: z.enum(['development', 'test', 'production']).optional().default('production'),
    SANITY_PROJECT_ID: z.string().min(8),
    SANITY_DATASET: z.string().min(6),
    // Azure specific
    AZ_ENV: z.enum(['d', 't', 'p']).optional().default('d'),
    DD_SERVICE: z.string().optional().default('nds-app-b2c-fe'),
    DD_ENV: z.string().optional().default('composable-nds-d'),
    DD_SITE: z.string().optional().default('datadoghq.eu'),
  },
  createFinalSchema: (shape) => z.object(shape).superRefine(requireAllRelewiseEnvOrNone),
})
